import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, TrendingUp, DollarSign, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Import tab components (we'll create these)
import TempsPresence from './rh/TempsPresence';
import Performances from './rh/Performances';
import Paie from './rh/Paie';

const RH = () => {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'temps');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingLeaves: 0,
    pendingReviews: 0,
    employeesCount: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchStats = async () => {
    try {
      if (user?.role === 'admin' || user?.role === 'sub_admin') {
        // Fetch pending leaves count
        const leavesResponse = await axios.get(`${API}/leave-requests?status=en_attente`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMountedRef.current) {
          setStats(prev => ({ ...prev, pendingLeaves: leavesResponse.data.length }));
        }
      }
      if (isMountedRef.current) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const tabs = [
    {
      id: 'temps',
      name: '⏰ Temps & Présence',
      icon: Clock,
      badge: stats.pendingLeaves > 0 ? stats.pendingLeaves : null,
      color: 'from-blue-600 to-cyan-600'
    },
    {
      id: 'performances',
      name: '📊 Performances',
      icon: TrendingUp,
      badge: stats.pendingReviews > 0 ? stats.pendingReviews : null,
      color: 'from-purple-600 to-pink-600'
    },
    {
      id: 'paie',
      name: '💰 Paie',
      icon: DollarSign,
      badge: null,
      color: 'from-green-600 to-emerald-600'
    }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="rh-container">
        {/* Header */}
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
            🏢 Gestion RH
          </h1>
          <p className="text-lg text-gray-600">
            Ressources Humaines - {user?.role === 'admin' ? 'Administrateur' : user?.role === 'sub_admin' ? 'Sous-Administrateur' : 'Employé'}
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    relative px-6 py-4 text-sm font-semibold transition-all duration-200
                    flex items-center gap-3 border-b-2
                    ${isActive
                      ? `border-red-600 bg-gradient-to-r ${tab.color} bg-clip-text text-transparent`
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }
                  `}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-red-600' : 'text-gray-500'}`} />
                  <span>{tab.name}</span>
                  {tab.badge && (
                    <Badge className="bg-red-600 text-white">
                      {tab.badge}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'temps' && <TempsPresence user={user} token={token} API={API} />}
          {activeTab === 'performances' && <Performances user={user} token={token} API={API} />}
          {activeTab === 'paie' && <Paie user={user} token={token} API={API} />}
        </div>
      </div>
    </Layout>
  );
};

export default RH;
